export type ForestInput<T> = { id: string; parentId: string | null; data: T }

export type Forest<T> = {
	readonly ids: readonly string[]
	readonly roots: readonly string[]
	readonly parentOf: ReadonlyMap<string, string | null>
	readonly childrenOf: ReadonlyMap<string, readonly string[]>
	readonly depthOf: ReadonlyMap<string, number>
	readonly dataOf: ReadonlyMap<string, T>
	readonly orphans: readonly string[]
	readonly duplicates: readonly string[]
	has(id: string): boolean
	ancestors(id: string): string[]
	descendants(id: string): string[]
	leaves(id: string): string[]
}

export type ForestParts<T> = {
	dataOf: ReadonlyMap<string, T>
	parentOf: Map<string, string | null>
	childrenOf: Map<string, string[]>
	roots: string[]
	orphans: string[]
	duplicates: string[]
}

const EMPTY: readonly string[] = []

/**
 * Turns already-linked relations into a Forest: computes BFS order and depth,
 * promotes nodes unreachable from any root (cycles) to roots, and attaches the
 * traversal helpers. Shared by `createForest` and `createSubforest`.
 */
export const assembleForest = <T>(parts: ForestParts<T>): Forest<T> => {
	const { dataOf, parentOf, childrenOf, roots, orphans, duplicates } = parts
	const ids: string[] = []
	const depthOf = new Map<string, number>()

	const queue: string[] = []
	for (const root of roots) {
		queue.push(root)
		depthOf.set(root, 0)
	}

	// Enqueue children with BFS: defined once, reused for both initial and cycle-recovery traversal
	const enqueueBfsNode = (id: string) => {
		ids.push(id)
		const depth = depthOf.get(id)!
		for (const child of childrenOf.get(id) ?? EMPTY) {
			depthOf.set(child, depth + 1)
			queue.push(child)
		}
	}

	for (let i = 0; i < queue.length; i++) {
		enqueueBfsNode(queue[i])
	}

	// Cycle guard: anything not reached from a root is detached and made a root.
	if (ids.length !== dataOf.size) {
		const orphanStartIndex = queue.length
		for (const id of dataOf.keys()) {
			if (depthOf.has(id)) continue
			const parent = parentOf.get(id) ?? null
			if (parent !== null) {
				childrenOf.set(
					parent,
					(childrenOf.get(parent) ?? []).filter((child) => child !== id)
				)
			}
			parentOf.set(id, null)
			roots.push(id)
			orphans.push(id)
			depthOf.set(id, 0)
			queue.push(id)
		}
		// Continue BFS with newly-promoted orphan roots using the same traversal logic
		for (let i = orphanStartIndex; i < queue.length; i++) {
			enqueueBfsNode(queue[i])
		}
	}

	const ancestors = (id: string) => {
		const out: string[] = []
		let current = parentOf.get(id) ?? null
		while (current !== null) {
			out.unshift(current)
			current = parentOf.get(current) ?? null
		}

		return out
	}
	const descendants = (id: string) => {
		const out: string[] = []
		const stack = [...(childrenOf.get(id) ?? EMPTY)].reverse()
		while (stack.length) {
			const current = stack.pop()!
			out.push(current)
			const children = childrenOf.get(current) ?? EMPTY
			for (let i = children.length - 1; i >= 0; i--) stack.push(children[i])
		}

		return out
	}
	const leaves = (id: string) => {
		if (!(childrenOf.get(id) ?? EMPTY).length) return dataOf.has(id) ? [id] : []

		return descendants(id).filter(
			(node) => !(childrenOf.get(node) ?? EMPTY).length
		)
	}

	return {
		ids,
		roots,
		parentOf,
		childrenOf,
		depthOf,
		dataOf,
		orphans,
		duplicates,
		has: (id) => dataOf.has(id),
		ancestors,
		descendants,
		leaves,
	}
}

/**
 * Builds an immutable index over a flat parent-linked list in O(N) plus the
 * sibling sort. Orphans (parent id not in the list) become roots and are
 * reported; duplicate ids keep the last occurrence and are reported.
 */
export const createForest = <T>(
	items: ForestInput<T>[],
	options: { sort?: (a: T, b: T) => number } = {}
): Forest<T> => {
	const dataOf = new Map<string, T>()
	const rawParent = new Map<string, string | null>()
	const duplicates: string[] = []
	for (const { id, parentId, data } of items) {
		if (dataOf.has(id)) duplicates.push(id)
		dataOf.set(id, data)
		rawParent.set(id, parentId)
	}

	const parentOf = new Map<string, string | null>()
	const childrenOf = new Map<string, string[]>()
	const roots: string[] = []
	const orphans: string[] = []
	for (const [id, parentId] of rawParent) {
		const parent =
			parentId !== null && parentId !== id && dataOf.has(parentId)
				? parentId
				: null
		if (parentId !== null && parent === null) orphans.push(id)
		parentOf.set(id, parent)
		if (parent === null) {
			roots.push(id)
		} else {
			const siblings = childrenOf.get(parent)
			if (siblings) siblings.push(id)
			else childrenOf.set(parent, [id])
		}
	}

	if (options.sort) {
		const sort = options.sort
		const compare = (a: string, b: string) =>
			sort(dataOf.get(a)!, dataOf.get(b)!)
		roots.sort(compare)
		for (const children of childrenOf.values()) children.sort(compare)
	}

	return assembleForest({
		dataOf,
		parentOf,
		childrenOf,
		roots,
		orphans,
		duplicates,
	})
}
